import {
  CA_DETECT_BATCH_SIZE,
  CA_DETECT_PRIORITY_HOSTNAMES,
  CA_DETECT_TIMEOUT_MS,
  createErrorResult,
  createSuccessResult,
  ERRORS,
  ICertificateAuthority,
  SimpleResult,
} from "@prostocheck/shared";

import { fetchCertificates } from "../certificates";
import { IDetectCertificateAuthorityParams } from "./ca-detect.models";

const hostnameOf = (cmpUrl: string): string | null => {
  try {
    return new URL(cmpUrl).hostname.toLowerCase();
  } catch {
    return null;
  }
};

/** Common issuers first, so a typical key is found within the first batch. */
export const orderCertificateAuthorities = (
  authorities: ICertificateAuthority[],
): ICertificateAuthority[] => {
  const rank = (authority: ICertificateAuthority): number => {
    const hostname = hostnameOf(authority.cmpUrl);
    const index = hostname
      ? CA_DETECT_PRIORITY_HOSTNAMES.indexOf(hostname)
      : -1;

    return index === -1 ? CA_DETECT_PRIORITY_HOSTNAMES.length : index;
  };

  return [...authorities].sort((a, b) => rank(a) - rank(b));
};

/**
 * A CMP request only ever asks "do you have this keyid" — the container and
 * the request carry no mention of the issuing ЦСК — so the only way to find
 * the right one is to ask each of them and stop at the first "found". See
 * ai/docs/kep-signing.md. `fetchCertificates` already tells "found" from
 * "not found" and only touches `keyBox` on a match, so it doubles as the probe.
 */
export const detectCertificateAuthority = async ({
  keyBox,
  authorities,
  batchSize = CA_DETECT_BATCH_SIZE,
  onProgress,
}: IDetectCertificateAuthorityParams): Promise<
  SimpleResult<ICertificateAuthority>
> => {
  const ordered = orderCertificateAuthorities(authorities);

  for (let start = 0; start < ordered.length; start += batchSize) {
    const batch = ordered.slice(start, start + batchSize);
    const results = await Promise.all(
      batch.map(async authority => ({
        authority,
        result: await fetchCertificates({
          keyBox,
          cmpUrl: authority.cmpUrl,
          timeoutMs: CA_DETECT_TIMEOUT_MS,
        }),
      })),
    );

    const hit = results.find(({ result }) => result.success);

    if (hit) {
      return createSuccessResult(hit.authority);
    }

    onProgress?.(Math.min(start + batchSize, ordered.length), ordered.length);
  }

  return createErrorResult(ERRORS.key.certificateAuthorityNotDetected);
};
